/**
 * useCUAConsent — hook & store for CUA Consent modal state
 * Reference: TASK-UI-CU-005-1, SOL-UI-CU-005
 */

import { create } from 'zustand';
import { useCUAGrantStore } from '../store/cuaGrantStore';
import type { PAType } from '../model/types';

interface CUAConsentState {
  visible: boolean;
  paMode: PAType;
  sessionId: string;
  openModal: (params?: { sessionId?: string; paMode?: PAType }) => void;
  showModal: (params?: { sessionId?: string; paMode?: PAType }) => void;
  closeModal: () => void;
  consent: () => Promise<void>;
  decline: () => void;
}

export const useCUAConsentStore = create<CUAConsentState>((set, get) => ({
  visible: false,
  paMode: 'pa2',
  sessionId: 'current-session',

  openModal: (params) => {
    set({
      visible: true,
      sessionId: params?.sessionId ?? get().sessionId,
      paMode: params?.paMode ?? get().paMode,
    });
  },

  showModal: (params) => {
    get().openModal(params);
  },

  closeModal: () => {
    set({ visible: false });
  },

  consent: async () => {
    const { sessionId, paMode } = get();
    await useCUAGrantStore.getState().grant(sessionId, paMode);
    set({ visible: false });
  },

  decline: () => {
    set({ visible: false });
  },
}));

export const cuaConsentStore = useCUAConsentStore;

export function useCUAConsent() {
  const visible = useCUAConsentStore((s) => s.visible);
  const paMode = useCUAConsentStore((s) => s.paMode);
  const sessionId = useCUAConsentStore((s) => s.sessionId);
  const openModal = useCUAConsentStore((s) => s.openModal);
  const closeModal = useCUAConsentStore((s) => s.closeModal);
  const consent = useCUAConsentStore((s) => s.consent);
  const decline = useCUAConsentStore((s) => s.decline);

  return {
    visible,
    paMode,
    sessionId,
    openModal,
    closeModal,
    consent,
    decline,
  };
}
